import moment from "moment";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { IPortalSyncSettingsModel } from "@/Portfolio/Models/UI/IPortalSyncSettingsModel";

interface IPortalSyncSettingsContainerProps {
    model: IPortalSyncSettingsModel;
    className?: string;
    columnOrderPopupOpen?: boolean;
    showStatuses?: boolean
}

interface IPortalSyncSettingsContainerState {
    model: IPortalSyncSettingsModel;
}

class PortalSyncSettingsContainer extends BaseReactComponent<IPortalSyncSettingsContainerProps, IPortalSyncSettingsContainerState> {
    constructor(props: IPortalSyncSettingsContainerProps) {
        super(props);
        this.state = {
            model: this.cloneModel(props.model)
        };
    }

    public componentDidMount(): void {
        super.setOverlay("PortalSyncSettingsContainer", { transparent: true, onClicked: () => this.handleSave() });
    }

    public render(): JSX.Element {
        const hasSites = this.props.model.get("Sites")?.count() > 1;

        return (
            <div className={this.props.className}>
                <table className="f-content">
                    <thead>
                        <tr>
                            <th>
                                <label style={{ display: "inline" }}>{cacheStore.GetString("DateLabel").toUpperCase()}</label>
                            </th>
                            {hasSites && (
                                <th>
                                    <CheckboxControl
                                        id="sites_check"
                                        label={cacheStore.GetString("SitesLabel").toUpperCase()}
                                        onChange={checked => this.onSelectAllSitesCheckedChanged(checked)}
                                        checked={this.state.model.get("SelectAllSites")}
                                    />
                                </th>
                            )}
                            <th>
                                <CheckboxControl
                                    id="clinicians_check"
                                    label={cacheStore.GetString("CliniciansLabel").toUpperCase()}
                                    onChange={checked => this.onSelectAllEmployeesCheckedChanged(checked)}
                                    checked={this.state.model.get("SelectAllEmployees")}
                                />
                            </th>
                            <th>
                                <CheckboxControl
                                    id="itemType_check"
                                    label={cacheStore.GetString("ItemTypeLabel").toUpperCase()}
                                    onChange={checked => this.onSelectAllItemTypesCheckedChanged(checked)}
                                    checked={this.state.model.get("SelectAllItemTypes")}
                                />
                            </th>
                            {this.props.showStatuses && (
                                <th>
                                    <CheckboxControl
                                        id="status_check"
                                        label={cacheStore.GetString("StatusLabel").toUpperCase()}
                                        onChange={checked => this.onSelectAllStatusesCheckedChanged(checked)}
                                        checked={this.state.model.get("SelectAllStatuses")}
                                    />
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <MultiDateSelector
                                    id="portalSync_settings_dateSelector"
                                    toChange={date => this.dateToChanged(date)}
                                    fromChange={date => this.dateFromChanged(date)}
                                    fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                    toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                    from={this.state.model.get("DateFrom")}
                                    to={this.state.model.get("DateTo")}
                                />
                            </td>
                            {hasSites && (
                                <td className="sites-column">
                                    <div style={{ maxHeight: "400px", minHeight: "400px", overflow: "auto" }}>
                                        <CheckedListControl
                                            DataSource={this.state.model.get("Sites")}
                                            onChange={(checked, id) => this.onSiteCheckedChanged(checked, id)}
                                            MaxTextLength={25}
                                        />
                                    </div>
                                </td>
                            )}
                            <td>
                                <input
                                    className="form-control"
                                    style={{ width: "225px" }}
                                    type="text"
                                    value={this.state.model.get("EmployeeSearchText")}
                                    onChange={(e: any) => this.onEmployeeSearchTextChanged(e.target.value)}
                                    placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                                />
                                <div style={{ maxHeight: "400px", overflow: "auto" }}>
                                    <CheckedListControl
                                        DataSource={this.state.model.get("Employees")}
                                        onChange={(checked, id) => this.onEmployeeCheckedChanged(checked, id)}
                                        sort
                                        limitTextWidth
                                        enableLazyRendering
                                    />
                                </div>
                            </td>
                            <td>
                                <CheckedListControl
                                    DataSource={this.state.model.get("ItemTypes")}
                                    onChange={(checked, id) => this.onItemTypeCheckedChanged(checked, id)}
                                />
                            </td>
                            {this.props.showStatuses && (
                                <td>
                                    <CheckedListControl
                                        DataSource={this.state.model.get("Statuses")}
                                        onChange={(checked, id) => this.onStatusCheckedChanged(checked, id)}
                                    />
                                </td>
                            )}
                        </tr>
                    </tbody>
                </table>
                <SearchSettingsFooter
                    onClose={() => this.handleCancel()}
                    onOk={() => this.handleSave()}
                    onColumnOrderingButtonClick={() => sessionStore.dispatch(LocalOperations.Portfolio.PortalSync_ColumnOrder_PopupOpen)}
                    onResultsPerPageChange={value => this.setModelState(this.state.model.set("PageSize", value))}
                    selectedPageSizeOption={this.state.model.get("PageSize")}
                    pageSizeOptions={this.props.model.get("PageSizeOptions").toArray()}
                />
            </div>
        );
    }

    private cloneModel(model: IPortalSyncSettingsModel): IPortalSyncSettingsModel {
        return model
            .set("Sites", model.get("Sites").toArray().map(p => p.clone()).toImmutable())
            .set("Employees", model.get("Employees").toArray().map(p => p.clone()).toImmutable())
            .set("ItemTypes", model.get("ItemTypes").toArray().map(p => p.clone()).toImmutable())
            .set("Statuses", model.get("Statuses").toArray().map(p => p.clone()).toImmutable());
    }

    private setModelState(model: IPortalSyncSettingsModel, clone: boolean = false): void {
        if (clone)
            model = this.cloneModel(model);

        this.setState({ model: model });
    }

    // hides any users not matching the selected sites or search text
    private updateEmployees(model: IPortalSyncSettingsModel): IPortalSyncSettingsModel {
        const checkedSiteIds = model.get("Sites").toArray().filter(p => p.Checked).map(p => p.Id);

        const allEmployees = model.get("Employees");
        let visibleEmployees = allEmployees.filterImmutable(employee => checkedSiteIds.length == 0 || employee.Sites.intersect(checkedSiteIds).any());

        const searchText = model.get("EmployeeSearchText");

        if (!IsNullOrWhiteSpace(searchText))
            visibleEmployees = SearchMenuHelper.FilterCliniciansByName(searchText, visibleEmployees);

        for (const employee of allEmployees.toArray()) {
            employee.Visible = false;
        }

        let anyVisibleChecked = false;
        let anyVisibleUnchecked = false;

        for (const user of visibleEmployees.toArray()) {
            user.Visible = true;

            anyVisibleChecked = anyVisibleChecked || user.Checked;
            anyVisibleUnchecked = anyVisibleUnchecked || !user.Checked;
        }

        return model
            .set("SelectAllEmployees", anyVisibleChecked && !anyVisibleUnchecked)
            .set("Employees", allEmployees.asImmutable());
    }

    // Date Change Events
    private dateToChanged(date: Date): void {
        const dateTo = date && moment(date).endOf("day").toDate();
        this.setModelState(this.state.model.set("DateTo", dateTo));
    }

    private dateFromChanged(date: Date): void {
        const dateFrom = date && moment(date).startOf("day").toDate();
        this.setModelState(this.state.model.set("DateFrom", dateFrom));
    }

    // Site Checked Eventes
    private onSelectAllSitesCheckedChanged(isChecked: boolean): void {
        const wasChecked = this.state.model.get("SelectAllSites");

        if (wasChecked === isChecked) return;

        const sites = this.state.model.get("Sites").toArray();
        for (const site of sites) {
            site.Checked = isChecked;
        }

        const model = this.state.model
            .set("SelectAllSites", isChecked)
            .set("Sites", sites.toImmutable());

        this.setModelState(this.updateEmployees(model));
    }

    private onSiteCheckedChanged(isChecked: boolean, checkboxId: string): void {
        const sites = this.state.model.get("Sites").toArray();
        const site = sites.firstOrDefault(p => p.Id === checkboxId);

        if (!site) return;

        site.Checked = isChecked;

        this.setModelState(this.updateEmployees(this.state.model
            .set("SelectAllSites", sites.all(p => p.Checked))
            .set("Sites", sites.toImmutable())
        ));
    }

    // Employee Checked Eventes
    private onSelectAllEmployeesCheckedChanged(isChecked: boolean): void {
        const wasChecked = this.state.model.get("SelectAllEmployees");

        if (wasChecked === isChecked) return;

        const users = this.state.model.get("Employees").toArray();

        for (const user of users.filter(p => p.Visible)) {
            user.Checked = isChecked;
        }

        this.setModelState(this.state.model
            .set("SelectAllEmployees", isChecked)
            .set("Employees", users.toImmutable())
        );
    }

    private onEmployeeSearchTextChangedTimeout: number = 0;
    private onEmployeeSearchTextChanged(value: string): void {
        const model = this.state.model.set("EmployeeSearchText", value);

        this.setModelState(model);

        if (this.onEmployeeSearchTextChangedTimeout) {
            clearTimeout(this.onEmployeeSearchTextChangedTimeout);
            this.onEmployeeSearchTextChangedTimeout = 0;
        }

        const self = this;
        this.onEmployeeSearchTextChangedTimeout = window.setTimeout(
            () => self.setModelState(self.updateEmployees(model)),
            250
        );
    }

    private onEmployeeCheckedChanged(isChecked: boolean, checkboxId: string): void {
        const employees = this.state.model.get("Employees").toArray();
        const employee = employees.firstOrDefault(p => p.Id === checkboxId);

        if (!employee) return;

        employee.Checked = isChecked;

        this.setModelState(
            this.state.model
                .set("SelectAllEmployees", employees.filter(p => p.Visible).all(p => p.Checked))
                .set("Employees", employees.toImmutable())
        );
    }

    // Item Type Checked Eventes
    private onSelectAllItemTypesCheckedChanged(isChecked: boolean): void {
        const wasChecked = this.state.model.get("SelectAllItemTypes");

        if (wasChecked === isChecked) return;

        const itemTypes = this.state.model.get("ItemTypes").toArray();

        for (const user of itemTypes) {
            user.Checked = isChecked;
        }

        this.setModelState(this.state.model
            .set("SelectAllItemTypes", isChecked)
            .set("ItemTypes", itemTypes.toImmutable())
        );
    }

    private onItemTypeCheckedChanged(isChecked: boolean, checkboxId: string): void {
        const itemTypes = this.state.model.get("ItemTypes").toArray();
        const itemType = itemTypes.firstOrDefault(p => p.Id === checkboxId);

        if (!itemType) return;

        itemType.Checked = isChecked;

        this.setModelState(
            this.state.model
                .set("SelectAllItemTypes", itemTypes.all(p => p.Checked))
                .set("ItemTypes", itemTypes.toImmutable())
        );
    }

    // Status Checked Eventes
    private onSelectAllStatusesCheckedChanged(isChecked: boolean): void {
        const wasChecked = this.state.model.get("SelectAllStatuses");

        if (wasChecked === isChecked) return;

        const statuses = this.state.model.get("Statuses").toArray();

        for (const user of statuses) {
            user.Checked = isChecked;
        }

        this.setModelState(this.state.model
            .set("SelectAllStatuses", isChecked)
            .set("Statuses", statuses.toImmutable())
        );
    }

    private onStatusCheckedChanged(isChecked: boolean, checkboxId: string): void {
        const statuses = this.state.model.get("Statuses").toArray();
        const status = statuses.firstOrDefault(p => p.Id === checkboxId);

        if (!status) return;

        status.Checked = isChecked;

        this.setModelState(
            this.state.model
                .set("SelectAllStatuses", statuses.all(p => p.Checked))
                .set("Statuses", statuses.toImmutable())
        );
    }

    private handleCancel(): void {
        LocalEventDispatcher.dispatch(new LocalEvent(LocalOperations.Portfolio.PortalSync_SearchSettings_CancelClick));

        this.setModelState(this.props.model, true);
    }

    private handleSave(): void {
        let model = this.state.model;

        const checkedSiteIds = model.get("Sites").filter(p => p.Checked).map(p => p.Id).toArray();
        const allEmployees = model.get("Employees").toArray();

        // uncheck any users not in the selected sites
        if (checkedSiteIds.length) {
            for (const employee of allEmployees) {
                if (!employee.Checked || employee.Sites.intersect(checkedSiteIds).any()) continue;

                employee.Checked = false;
            }

            model = model.set("Employees", allEmployees.toImmutable());
        }

        LocalEvent.Dispatch(
            LocalOperations.Portfolio.PortalSync_SearchSettings_OkClick,
            {
                PortalSyncSettings: model
            }
        );

        this.setModelState(model, true);
    }
}

export { PortalSyncSettingsContainer };
